import React, { useState, useEffect, useImperativeHandle, useRef } from "react";
import {
	ChevronLeft,
	ChevronRight,
	ChevronDown,
	Search,
	Calendar,
	X,
	Filter,
} from "lucide-react";
import {
	CircularProgress,
	Typography,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format } from "date-fns";
import { enGB } from "date-fns/locale";
import { getApiBaseUrl } from "../../utils/apiConfig";
import { apiClient } from "../../services/api";

// Custom CSS for VFF Sidebar scrollbar - Always visible when needed
const vffSidebarScrollbarStyles = `
  .vff-sidebar-content::-webkit-scrollbar {
    width: 14px;
    background: #f8f8f8;
  }
  .vff-sidebar-content::-webkit-scrollbar-track {
    background: #f8f8f8;
    border-radius: 8px;
    border: 1px solid #d8d8d8;
  }
  .vff-sidebar-content::-webkit-scrollbar-thumb {
    background: grey;
    border-radius: 8px;
    border: 2px solid #f8f8f8;
    min-height: 40px;
    transition: all 0.2s ease;
  }
  .vff-sidebar-content::-webkit-scrollbar-thumb:hover {
    background: grey;
    border: 1px solid #f8f8f8;
    cursor: pointer;
  }
  .vff-sidebar-content::-webkit-scrollbar-thumb:active {
    background: grey;
  }
  .vff-sidebar-content::-webkit-scrollbar-corner {
    background: #f8f8f8;
  }
  
  /* Enhanced scrollbar visibility for Firefox */
  .vff-sidebar-content {
    scrollbar-width: auto !important;
    scrollbar-color: grey #f8f8f8 !important;
  }
  
  /* Add scroll indicators */
  .vff-sidebar-scroll-indicator-top,
  .vff-sidebar-scroll-indicator-bottom {
    position: sticky;
    left: 0;
    right: 0;
    height: 8px;
    pointer-events: none;
    z-index: 10;
    transition: opacity 0.3s ease;
  }
  
  .vff-sidebar-scroll-indicator-top {
    top: 0;
  }
    .vff-sidebar-scroll-indicator-bottom {
    bottom: 0;
  }
`;

// Inject the scrollbar styles into the document head
if (typeof document !== "undefined") {
	const existingStyle = document.getElementById("vff-sidebar-scrollbar-styles");
	if (!existingStyle) {
		const style = document.createElement("style");
		style.id = "vff-sidebar-scrollbar-styles";
		style.textContent = vffSidebarScrollbarStyles;
		document.head.appendChild(style);
	}
}

// Part number prefixes for filtering
const PART_NO_PREFIXES = [
	"P",
	"V",
	"92.",
	"81.",
	"06.",
	"U1.",
	"04.",
	"03.",
	"01.",
	"07.",
	"51.",
	"K",
	"M",
	"82.",
	"85.",
	"98.",
	"99.",
];

// Complete archive options - always available regardless of database content
const ARCHIVE_OPTIONS = [
	"Unarchived",
	"To be deleted",
	"Duplicate Entry",
	"No Demand",
	"Hausteile",
	"On Hold",
	"PAT",
	"Others",
];

// Filter keys that are scoped dynamically to the selected Programm(s)
const DYNAMIC_FILTER_KEYS = [
	"projectNumber",
	"fzgNr",
	"bza",
	"statusColor",
	"kategorieTeilenanflug",
	"supplier",
	"statusBtt",
	"ruckmeldeStat",
	"alg",
	"kastKeyAnlage",
	"anlo",
	"responsibleParts",
];

// Small inline spinner shown next to secondary-data labels while Phase 2 loads
const SecondaryLoadingDot = ({ show }) => {
	if (!show) return null;
	return (
		<CircularProgress
			size={11}
			thickness={5}
			sx={{
				color: "#cc0000",
				marginLeft: "6px",
				verticalAlign: "middle",
				flexShrink: 0,
			}}
		/>
	);
};

const VFFSidebar = React.forwardRef(
	({ onFiltersChange, onClearFilters, topPadding = 0 }, ref) => {
		const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
		const [isHovered, setIsHovered] = useState(false);
		const [isModalOpen, setIsModalOpen] = useState(false);
		const [filterOptions, setFilterOptions] = useState({
			programms: [],
			fzgNr: [],
			bza: [],
			partNo: [],
			projectNumber: [],
			supplier: [],
			baukastenNo: [],
			statusColor: [],
			statusBtt: [],
			kategorieTeilenanflug: [],
			purchasingStatuses: [],
			logistics2Statuses: [],
			ruckmeldeStat: [],
			alg: [],
			seTeam: [],
			pet: { from: "", to: "" },
			changeNo: [],
			kastKeyAnlage: [],
			anlo: [],
			vkgrArt: [],
			benenn: [],
			vkgrBend: [],
			mbKey: [],
			tags: [],
			responsibleParts: [],
			partsStatus: [],
			flagging: [],
			archive: [],
			programmFzgNrMapping: {},
			programmBzaMapping: {},
			programmStatusColorMapping: {},
			programmKategorieMapping: {},
			programmSupplierMapping: {},
			programmBaukastenMapping: {},
			programmBttStatusMapping: {},
			programmRuckmeldStatMapping: {},
			programmAlgMapping: {},
			programmKastKeyAnlageMapping: {},
			programmAnloMapping: {},
			programmResponsiblePartsMapping: {},
			programmProjectNumberMapping: {},
			projectNumberFzgNrMapping: {},
			projectNumberBzaMapping: {},
			projectNumberStatusColorMapping: {},
			projectNumberSupplierMapping: {},
			projectNumberBaukastenMapping: {},
			projectNumberKategorieMapping: {},
			projectNumberBttStatusMapping: {},
			projectNumberRuckmeldStatMapping: {},
			projectNumberAlgMapping: {},
			projectNumberKastKeyAnlageMapping: {},
			projectNumberAnloMapping: {},
			projectNumberResponsiblePartsMapping: {},
		});
		const [isLoading, setIsLoading] = useState(true);
		const [isSecondaryLoading, setIsSecondaryLoading] = useState(true);
		const [error, setError] = useState(null);
		const [hasScrollableContent, setHasScrollableContent] = useState(false);
		const [canScrollUp, setCanScrollUp] = useState(false);
		const [canScrollDown, setCanScrollDown] = useState(false);
		const contentRef = useRef(null);
		const [openDropdowns, setOpenDropdowns] = useState({
			programm: false,
			purchasing: false,
			logistics2: false,
			fzgNr: false,
			bza: false,
			partNo: false,
			projectNumber: false,
			partNoPrefix: false,
			supplier: false,
			baukastenNo: false,
			statusColor: false,
			statusBtt: false,
			kategorieTeilenanflug: false,
			ruckmeldeStat: false,
			alg: false,
			pet: false,
			changeNo: false,
			kastKeyAnlage: false,
			anlo: false,
			vkgrArt: false,
			benenn: false,
			vkgrBend: false,
			seTeam: false,
			responsible: false,
			mbKey: false,
			tags: false,
			responsibleParts: false,
			requirementDate: false,
			partsStatus: false,
			partsFollowupDate: false,
			ltbDate: false,
			flagging: false,
			archive: false,
		});

		// Search functionality
		const [searchTerms, setSearchTerms] = useState({});
		const [debouncedSearchTerms, setDebouncedSearchTerms] = useState({});
		// Requirement Date filter state
		const [requirementDateFrom, setRequirementDateFrom] = useState(null);
		const [requirementDateTo, setRequirementDateTo] = useState(null);

		// Parts Followup Date filter state
		const [partsFollowupDateFrom, setPartsFollowupDateFrom] = useState(null);
		const [partsFollowupDateTo, setPartsFollowupDateTo] = useState(null);

		// LTB Date filter state
		const [ltbDateFrom, setLtbDateFrom] = useState(null);
		const [ltbDateTo, setLtbDateTo] = useState(null);
		const [selectedFilters, setSelectedFilters] = useState({
			programm: [],
			purchasing: [],
			logistics2: [],
			fzgNr: [],
			bza: [],
			partNo: [],
			projectNumber: [],
			partNoPrefix: [],
			supplier: [],
			baukastenNo: [],
			statusColor: [],
			statusBtt: [],
			kategorieTeilenanflug: [],
			ruckmeldeStat: [],
			alg: [],
			seTeam: [],
			mbKey: [],
			tags: [],
			pet: { from: "", to: "" },
			changeNo: [],
			kastKeyAnlage: [],
			anlo: [],
			vkgrArt: [],
			benenn: [],
			responsibleParts: [],
			partsStatus: [],
			flagging: [],
			archive: [],
			requirementDateFrom: null,
			requirementDateTo: null,
			partsFollowupDateFrom: null,
			partsFollowupDateTo: null,
			ltbDateFrom: null,
			ltbDateTo: null,
		});
		const fetchFilterOptions = async () => {
			try {
				setIsLoading(true);
				setError(null);

				// Phase 1 & 2 fire simultaneously — sidebar renders as soon as primary arrives
				const primaryPromise = apiClient.get("/api/vff-filter-options/primary");
				const fullPromise = apiClient.get("/api/vff-filter-options");

				// Phase 1: render sidebar immediately with primary options (programm, fzgNr, bza)
				const primaryRes = await primaryPromise;
				const primaryData = primaryRes.data;
				setFilterOptions((prev) => ({
					...prev,
					programms: primaryData.programms || [],
					fzgNr: primaryData.fzgNr || [],
					bza: primaryData.bza || [],
					projectNumber: primaryData.projectNumber || [],
					programmFzgNrMapping: primaryData.programmFzgNrMapping || {},
					programmBzaMapping: primaryData.programmBzaMapping || {},
					programmProjectNumberMapping:
						primaryData.programmProjectNumberMapping || {},
				}));
				setIsLoading(false); // sidebar is now interactive
				console.log(
					"✅ Phase 1 complete — sidebar rendered with primary filters",
				);
				// Phase 2: fill in remaining dropdowns in background
				fullPromise
					.then((fullRes) => {
						const data = fullRes.data;
						setFilterOptions((prev) => ({
							...prev,
							...data,
							programmFzgNrMapping:
								data.programmFzgNrMapping || prev.programmFzgNrMapping,
							programmBzaMapping:
								data.programmBzaMapping || prev.programmBzaMapping,
							programmStatusColorMapping: data.programmStatusColorMapping || {},
							programmKategorieMapping: data.programmKategorieMapping || {},
							programmSupplierMapping: data.programmSupplierMapping || {},
							programmBaukastenMapping: data.programmBaukastenMapping || {},
							programmBttStatusMapping: data.programmBttStatusMapping || {},
							programmRuckmeldStatMapping:
								data.programmRuckmeldStatMapping || {},
							programmAlgMapping: data.programmAlgMapping || {},
							programmKastKeyAnlageMapping:
								data.programmKastKeyAnlageMapping || {},
							programmAnloMapping: data.programmAnloMapping || {},
							programmResponsiblePartsMapping:
								data.programmResponsiblePartsMapping || {},
							programmProjectNumberMapping:
								data.programmProjectNumberMapping || {},
							projectNumberFzgNrMapping: data.projectNumberFzgNrMapping || {},
							projectNumberBzaMapping: data.projectNumberBzaMapping || {},
							projectNumberStatusColorMapping:
								data.projectNumberStatusColorMapping || {},
							projectNumberSupplierMapping:
								data.projectNumberSupplierMapping || {},
							projectNumberBaukastenMapping:
								data.projectNumberBaukastenMapping || {},
							projectNumberKategorieMapping:
								data.projectNumberKategorieMapping || {},
							projectNumberBttStatusMapping:
								data.projectNumberBttStatusMapping || {},
							projectNumberRuckmeldStatMapping:
								data.projectNumberRuckmeldStatMapping || {},
							projectNumberAlgMapping: data.projectNumberAlgMapping || {},
							projectNumberKastKeyAnlageMapping:
								data.projectNumberKastKeyAnlageMapping || {},
							projectNumberAnloMapping: data.projectNumberAnloMapping || {},
							projectNumberResponsiblePartsMapping:
								data.projectNumberResponsiblePartsMapping || {},
						}));
						console.log("✅ Phase 2 complete — all filter options loaded");
					})
					.catch((err) => {
						console.warn("⚠️ Phase 2 (full filter options) failed:", err);
					})
					.finally(() => {
						setIsSecondaryLoading(false);
					});

				return primaryData;
			} catch (error) {
				if (error.name !== "AbortError") {
					setError("Failed to load filter options");
					console.error("Error fetching filter options:", error);
				}
				return null;
			} finally {
				setIsLoading(false);
			}
		};

		// Add request deduplication logic
		const pendingRequest = React.useRef(null);

		useEffect(() => {
			// Prevent duplicate requests by reusing existing promise
			if (!pendingRequest.current) {
				pendingRequest.current = fetchFilterOptions();
				// Clear the reference when the promise resolves
				pendingRequest.current.finally(() => {
					pendingRequest.current = null;
				});
			}
			return () => {
				// We don't need to abort since we're reusing the same promise
			};
		}, []);
		const toggleDropdown = (dropdownName) => {
			setOpenDropdowns((prev) => {
				const isCurrentlyOpen = prev[dropdownName];
				const closedDropdowns = Object.keys(prev).reduce(
					(acc, key) => ({ ...acc, [key]: false }),
					{},
				);
				return { ...closedDropdowns, [dropdownName]: !isCurrentlyOpen };
			});
		};

		const toggleModal = () => {
			setIsModalOpen(!isModalOpen);
		};

		const handleFilterToggle = (filterName, value) => {
			setSelectedFilters((prev) => {
				const currentValues = prev[filterName] || [];
				const isSelected = currentValues.includes(value);
				const newValues = isSelected
					? currentValues.filter((v) => v !== value)
					: [...currentValues, value];

				let newFilters = { ...prev, [filterName]: newValues };
				// Reset dependent filters when parent filters change
				if (filterName === "programm") {
					// When programm changes, reset dependent filters
					console.log(
						"Programm selection changed, resetting dependent filters (fzgNr and bza)",
					);
					newFilters.fzgNr = [];
					newFilters.bza = [];

					// Show a toast or some UI feedback that filters were reset
					// This would be implemented with a toast library or custom notification
					if (newValues.length > 0) {
						console.log(
							`Dropdown options for fzgNr and bza will now be filtered to match selected programm(s): ${newValues.join(", ")}`,
						);
					} else {
						console.log(
							"All programms deselected, fzgNr and bza dropdowns will show all available options",
						);
					}
				}

				return newFilters;
			});
		};
		const getFilterDropdownText = (filterName) => {
			const selected = selectedFilters[filterName] || [];
			const labels = {
				programm: "Select Programm",
				purchasing: "Select Purchasing",
				logistics2: "Select Logistics",
				fzgNr: "Select Fzg Nr",
				bza: "Select BZA",
				partNo: "Select Part No",
				projectNumber: "Select Project Number",
				partNoPrefix: "Select Part No Prefix",
				supplier: "Select Supplier",
				baukastenNo: "Select Baukasten No",
				statusColor: "Select Status Color",
				statusBtt: "Select BTT Status",
				kategorieTeilenanflug: "Select Kategorie Teileanflug",
				ruckmeldeStat: "Select Ruckmelde Status",
				alg: "Select ALG",
				seTeam: "Select SET",
				mbKey: "Select MB Key",
				tags: "Select Tags",
				pet: "Select PET",
				changeNo: "Select Change No",
				kastKeyAnlage: "Select Kast Key Anlage",
				anlo: "Select Anlo",
				vkgrArt: "Select VKGR Art",
				benenn: "Select Benenn",
				vkgrBend: "Select VKGR Bend",
				responsibleParts: "Select Person",
				partsStatus: "Select Parts Status",
				flagging: "Select Flagging",
				archive: "Select Archive",
			};

			// Add indication for dynamically filtered fields
			let baseLabel = labels[filterName];
			const isDynamicallyFiltered =
				(filterName === "fzgNr" || filterName === "bza") &&
				selectedFilters.programm &&
				selectedFilters.programm.length > 0;

			if (isDynamicallyFiltered && selected.length === 0) {
				baseLabel += " (filtered)";
			}

			if (selected.length === 0) return baseLabel;
			if (selected.length === 1) return selected[0];
			return `${selected.length} items selected`;
		};
		const clearFilters = () => {
			const emptyFilters = {
				programm: [],
				purchasing: [],
				logistics2: [],
				fzgNr: [],
				bza: [],
				partNo: [],
				projectNumber: [],
				partNoPrefix: [],
				supplier: [],
				baukastenNo: [],
				statusColor: [],
				statusBtt: [],
				kategorieTeilenanflug: [],
				ruckmeldeStat: [],
				alg: [],
				seTeam: [],
				pet: { from: "", to: "" },
				changeNo: [],
				kastKeyAnlage: [],
				anlo: [],
				vkgrArt: [],
				benenn: [],
				vkgrBend: [],
				mbKey: [],
				tags: [],
				responsibleParts: [],
				partsStatus: [],
				flagging: [],
				archive: [],
				globalSearch: "",
				requirementDateFrom: null,
				requirementDateTo: null,
				partsFollowupDateFrom: null,
				partsFollowupDateTo: null,
				ltbDateFrom: null,
				ltbDateTo: null,
			}; // Reset local state first
			setSelectedFilters({
				programm: [],
				purchasing: [],
				logistics2: [],
				fzgNr: [],
				bza: [],
				partNo: [],
				projectNumber: [],
				partNoPrefix: [],
				supplier: [],
				baukastenNo: [],
				statusColor: [],
				statusBtt: [],
				kategorieTeilenanflug: [],
				ruckmeldeStat: [],
				alg: [],
				seTeam: [],
				pet: { from: "", to: "" },
				changeNo: [],
				kastKeyAnlage: [],
				anlo: [],
				vkgrArt: [],
				benenn: [],
				vkgrBend: [],
				mbKey: [],
				tags: [],
				responsibleParts: [],
				partsStatus: [],
				flagging: [],
				archive: [],
				requirementDateFrom: null,
				requirementDateTo: null,
				partsFollowupDateFrom: null,
				partsFollowupDateTo: null,
				ltbDateFrom: null,
				ltbDateTo: null,
			});
			setGlobalSearch("");
			setSearchTerms({});
			setDebouncedSearchTerms({});
			setRequirementDateFrom(null);
			setRequirementDateTo(null);
			setPartsFollowupDateFrom(null);
			setPartsFollowupDateTo(null);
			setLtbDateFrom(null);
			setLtbDateTo(null);
			console.log("Clearing all filters from sidebar");

			// Call onClearFilters to properly reset all state including archived parts mode
			if (onClearFilters) {
				console.log("Calling onClearFilters to reset all state");
				onClearFilters();
			}
		}; // Helper function to format date to YYYY-MM-DD format without timezone issues
		const formatDateForBackend = (date) => {
			if (!date) return null;
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, "0");
			const day = String(date.getDate()).padStart(2, "0");
			return `${year}-${month}-${day}`;
		};
		// Helper function to map German ampel color names to actual colors
		const getAmpelColor = (ampelValue) => {
			if (!ampelValue || ampelValue === "-") return "transparent";

			// Convert to lowercase for case-insensitive comparison
			const value = ampelValue.toString().toLowerCase().trim();

			switch (value) {
				case "tra":
				case "transparent":
					return "transparent";
				case "blau":
				case "blue":
					return "#4b96d2"; // blue
				case "gelb":
				case "yellow":
					return "#ffff00"; // delivery date missing
				case "grün":
				case "grun":
				case "green":
					return "#91b900"; // green
				case "rot":
				case "red":
					return "#e40045"; // red
				case "grau":
				case "grey":
				case "gray":
					return "#6b7280"; // gray
				case "lila":
				case "purple":
					return "#800080"; // purple
				case "orange":
					return "#ffcd00"; // nomination missing
				case "available":
					return "#137c13"; // dark green for available
				case "light green":
					return "#91b900"; // light green
				default:
					return "transparent";
			}
		};

		// Helper function to get descriptive labels for status colors
		const getStatusColorLabel = (colorValue) => {
			if (!colorValue) return "";

			// Convert to lowercase for case-insensitive comparison
			const value = colorValue.toString().toLowerCase().trim();

			switch (value) {
				case "blau":
				case "blue":
					return "Alternatives";
				case "yellow":
					return "Delivery date missing";
				case "rot":
				case "red":
					return "Delayed";
				case "gelb":
				case "orange":
					return "Nomination missing";
				case "light green":
					return "Available for build";
				case "grün":
				case "grun":
				case "green":
					return "On time";
				case "grau":
				case "grey":
				case "gray":
					return "Unknown status";
				case "lila":
				case "purple":
					return "Special attention";
				case "tra":
				case "transparent":
					return "no status";
				case "available":
					return "ready";
				default:
					return "";
			}
		};
		const applyFilters = () => {
			// Build filters object excluding empty arrays and null/undefined values
			const filtersToApply = Object.entries(selectedFilters).reduce(
				(acc, [key, value]) => {
					if (
						key === "requirementDateFrom" ||
						key === "requirementDateTo" ||
						key === "partsFollowupDateFrom" ||
						key === "partsFollowupDateTo" ||
						key === "ltbDateFrom" ||
						key === "ltbDateTo"
					) {
						// Skip date filters in this section - handle them separately
						return acc;
					}

					// Handle pet range object
					if (key === "pet") {
						acc[key] = value; // always pass the object, even if empty
						return acc;
					}

					if (Array.isArray(value) && value.length > 0) {
						acc[key] = value;
					}
					return acc;
				},
				{},
			);

			// Add date filters with proper formatting
			if (requirementDateFrom) {
				filtersToApply.requirementDateFrom =
					formatDateForBackend(requirementDateFrom);
			}
			if (requirementDateTo) {
				filtersToApply.requirementDateTo =
					formatDateForBackend(requirementDateTo);
			}
			if (partsFollowupDateFrom) {
				filtersToApply.partsFollowupDateFrom = formatDateForBackend(
					partsFollowupDateFrom,
				);
			}
			if (partsFollowupDateTo) {
				filtersToApply.partsFollowupDateTo =
					formatDateForBackend(partsFollowupDateTo);
			}
			if (ltbDateFrom) {
				filtersToApply.ltbDateFrom = formatDateForBackend(ltbDateFrom);
			}
			if (ltbDateTo) {
				filtersToApply.ltbDateTo = formatDateForBackend(ltbDateTo);
			}

			// Include requirement date filters from state
			if (requirementDateFrom) {
				filtersToApply.requirementDateFrom =
					formatDateForBackend(requirementDateFrom);
			}
			if (requirementDateTo) {
				filtersToApply.requirementDateTo =
					formatDateForBackend(requirementDateTo);
			}

			// Include parts followup date filters from state
			if (partsFollowupDateFrom) {
				filtersToApply.partsFollowupDateFrom = formatDateForBackend(
					partsFollowupDateFrom,
				);
			}
			if (partsFollowupDateTo) {
				filtersToApply.partsFollowupDateTo =
					formatDateForBackend(partsFollowupDateTo);
			}
			// Include global search if it exists
			if (globalSearch) {
				filtersToApply.globalSearch = globalSearch;
			}
			// Collapse sidebar after applying filters
			setIsSidebarCollapsed(true);
			setIsModalOpen(false);
			onFiltersChange(filtersToApply);
		};
		useEffect(() => {
			const handleClickOutside = (event) => {
				if (
					!event.target.closest(".custom-dropdown") &&
					!event.target.closest(".accordion-panel")
				) {
					setOpenDropdowns(
						Object.keys(openDropdowns).reduce(
							(acc, key) => ({ ...acc, [key]: false }),
							{},
						),
					);
				}
			};
			document.addEventListener("mousedown", handleClickOutside);
			return () =>
				document.removeEventListener("mousedown", handleClickOutside);
		}, [openDropdowns]);
		// Debounce effect for search terms
		useEffect(() => {
			const handler = setTimeout(() => {
				setDebouncedSearchTerms(searchTerms);
			}, 250); // 250ms debounce
			return () => clearTimeout(handler);
		}, [searchTerms]);
		// Enhanced scroll detection effect
		useEffect(() => {
			const checkScrollable = () => {
				if (contentRef.current) {
					const { scrollHeight, clientHeight, scrollTop } = contentRef.current;
					const hasOverflow = scrollHeight > clientHeight;

					setHasScrollableContent(hasOverflow);
					setCanScrollUp(scrollTop > 0);
					setCanScrollDown(scrollTop < scrollHeight - clientHeight);
				}
			};

			const handleScroll = () => {
				checkScrollable();
			};

			// Check on mount and when content changes
			checkScrollable();

			// Add scroll listener
			if (contentRef.current) {
				contentRef.current.addEventListener("scroll", handleScroll, {
					passive: true,
				});
			}

			// Add resize observer to detect content changes
			const resizeObserver = new ResizeObserver(checkScrollable);
			if (contentRef.current) {
				resizeObserver.observe(contentRef.current);
			}

			// Add window resize listener as backup
			window.addEventListener("resize", checkScrollable);

			return () => {
				if (contentRef.current) {
					contentRef.current.removeEventListener("scroll", handleScroll);
				}
				resizeObserver.disconnect();
				window.removeEventListener("resize", checkScrollable);
			};
		}, [isLoading, error, selectedFilters, openDropdowns]);

		// Additional effect to check scrollable content after initial load
		useEffect(() => {
			if (!isLoading && contentRef.current) {
				// Small delay to ensure DOM has settled
				const timer = setTimeout(() => {
					if (contentRef.current) {
						const { scrollHeight, clientHeight } = contentRef.current;
						setHasScrollableContent(scrollHeight > clientHeight);
					}
				}, 100);

				return () => clearTimeout(timer);
			}
		}, [isLoading, filterOptions]); // Dynamic filtering logic — scopes options to selected programm(s) when a mapping exists
		const getFilteredOptions = (filterType) => {
			const selectedProgramms = selectedFilters.programm || [];
			const selectedProjectNumbers = selectedFilters.projectNumber || [];

			// Scope a flat list by programm selection via a mapping dict, then further
			// intersect with projectNumber selection if any project numbers are selected.
			const scopeToProgram = (
				allOptions,
				programmMappingKey,
				projectNumberMappingKey,
			) => {
				let result = allOptions;

				// Step 1 — narrow by selected programm(s)
				if (selectedProgramms.length > 0) {
					const programmMapping = filterOptions[programmMappingKey] || {};
					const union = [
						...new Set(
							selectedProgramms.flatMap((p) => programmMapping[p] || []),
						),
					];
					result = union.length > 0 ? union.sort() : allOptions;
				}

				// Step 2 — further narrow by selected project number(s), if applicable
				if (selectedProjectNumbers.length > 0 && projectNumberMappingKey) {
					const projMapping = filterOptions[projectNumberMappingKey] || {};
					const projUnion = [
						...new Set(
							selectedProjectNumbers.flatMap((p) => projMapping[p] || []),
						),
					];
					if (projUnion.length > 0) {
						const projSet = new Set(projUnion);
						result = result.filter((v) => projSet.has(v));
						if (result.length === 0) result = projUnion.sort(); // fallback: show proj-scoped list
					}
				}

				return result;
			};

			switch (filterType) {
				case "projectNumber":
					return scopeToProgram(
						filterOptions.projectNumber || [],
						"programmProjectNumberMapping",
						null,
					);
				case "fzgNr":
					return scopeToProgram(
						filterOptions.fzgNr || [],
						"programmFzgNrMapping",
						"projectNumberFzgNrMapping",
					);
				case "bza":
					return scopeToProgram(
						filterOptions.bza || [],
						"programmBzaMapping",
						"projectNumberBzaMapping",
					);
				case "statusColor":
					return scopeToProgram(
						filterOptions.statusColor || [],
						"programmStatusColorMapping",
						"projectNumberStatusColorMapping",
					);
				case "kategorieTeilenanflug":
					return scopeToProgram(
						filterOptions.kategorieTeilenanflug || [],
						"programmKategorieMapping",
						"projectNumberKategorieMapping",
					);
				case "supplier": {
					const supplierOptions = scopeToProgram(
						filterOptions.supplier || [],
						"programmSupplierMapping",
						"projectNumberSupplierMapping",
					);
					const withoutDash = supplierOptions.filter((s) => s !== "-");
					return ["-", ...withoutDash];
				}
				case "baukastenNo":
					// Flat (non-programm-scoped) list — cached server-side; too large to scope
					return filterOptions.baukastenNo || [];
				case "statusBtt":
					return scopeToProgram(
						filterOptions.statusBtt || [],
						"programmBttStatusMapping",
						"projectNumberBttStatusMapping",
					);
				case "ruckmeldeStat":
					return scopeToProgram(
						filterOptions.ruckmeldeStat || [],
						"programmRuckmeldStatMapping",
						"projectNumberRuckmeldStatMapping",
					);
				case "alg":
					return scopeToProgram(
						filterOptions.alg || [],
						"programmAlgMapping",
						"projectNumberAlgMapping",
					);
				case "kastKeyAnlage":
					return scopeToProgram(
						filterOptions.kastKeyAnlage || [],
						"programmKastKeyAnlageMapping",
						"projectNumberKastKeyAnlageMapping",
					);
				case "anlo":
					return scopeToProgram(
						filterOptions.anlo || [],
						"programmAnloMapping",
						"projectNumberAnloMapping",
					);
				case "responsibleParts":
					return scopeToProgram(
						filterOptions.responsibleParts || [],
						"programmResponsiblePartsMapping",
						"projectNumberResponsiblePartsMapping",
					);
				case "programm":
					return filterOptions.programms || [];
				case "partNo":
					return filterOptions.partNo || [];
				case "partNoPrefix":
					return PART_NO_PREFIXES;
				case "mbKey":
					return filterOptions.mbKey || [];
				default:
					return filterOptions[filterType] || [];
			}
		};

		// Monitor programm selection changes to provide feedback about dynamic filtering
		useEffect(() => {
			const programmSelection = selectedFilters.programm || [];
			if (programmSelection.length > 0) {
				console.log(
					`🔄 Dynamic filtering active for [${programmSelection.join(", ")}]`,
				);
			} else {
				console.log("🔄 Dynamic filtering disabled: no programm selected");
			}
		}, [
			selectedFilters.programm,
			filterOptions.programmFzgNrMapping,
			filterOptions.programmBzaMapping,
		]);
		// Handle search input change
		const handleSearchChange = (filterName, value) => {
			setSearchTerms((prev) => ({ ...prev, [filterName]: value }));
		};

		// Select All / Deselect All for a given filter
		const handleSelectAll = (filterName, filteredOptions) => {
			setSelectedFilters((prev) => {
				const current = prev[filterName] || [];
				const allSelected = filteredOptions.every((o) => current.includes(o));
				if (allSelected) {
					return {
						...prev,
						[filterName]: current.filter((v) => !filteredOptions.includes(v)),
					};
				} else {
					const merged = Array.from(new Set([...current, ...filteredOptions]));
					return { ...prev, [filterName]: merged };
				}
			});
		};

		// Render status dropdown without special blank value handling (for most filters)
		const renderStatusDropdown = (
			filterName,
			options,
			label,
			showLoadingDot = false,
		) => {
			const searchValue = debouncedSearchTerms[filterName] || "";
			const safeOptions = Array.isArray(options) ? options : [];
			const filteredOptions = searchValue
				? safeOptions.filter(
						(option) =>
							option &&
							option.toLowerCase().includes(searchValue.toLowerCase()),
					)
				: safeOptions;
			const _hasProgramm = (selectedFilters.programm || []).length > 0;
			const _hasProjNum =
				filterName !== "projectNumber" &&
				(selectedFilters.projectNumber || []).length > 0;
			const isDynamic =
				DYNAMIC_FILTER_KEYS.includes(filterName) &&
				(_hasProgramm || _hasProjNum);
			return (
				<div style={styles.filterGroup}>
					<label
						style={{
							...styles.filterLabel,
							display: "flex",
							alignItems: "center",
							flexWrap: "wrap",
							gap: "4px",
						}}
					>
						{label}
						{isDynamic && (
							<Filter
								style={{
									width: "12px",
									height: "12px",
									color: "#cc0000",
									marginLeft: "4px",
									flexShrink: 0,
								}}
								title={
									_hasProgramm && _hasProjNum
										? "Filtered by programm & project number"
										: _hasProjNum
											? "Filtered by project number"
											: "Filtered by programm"
								}
							/>
						)}
						<SecondaryLoadingDot show={showLoadingDot} />
					</label>
					<div style={styles.customDropdown} className="custom-dropdown">
						<div
							style={styles.dropdownButton}
							onClick={() => toggleDropdown(filterName)}
							role="button"
							tabIndex={0}
							onKeyDown={(e) => e.key === "Enter" && toggleDropdown(filterName)}
						>
							<span
								style={{
									color:
										(selectedFilters[filterName] || []).length === 0
											? "#999"
											: "#333",
									display: "inline-block",
									width: "calc(100% - 20px)",
								}}
							>
								{getFilterDropdownText(filterName)}
							</span>
							<span style={{ float: "right", marginTop: "2px" }}>
								<ChevronDown
									style={{ width: "16px", height: "16px", color: "#666" }}
								/>
							</span>
						</div>
						{openDropdowns[filterName] && (
							<div style={styles.dropdownContent}>
								<div style={styles.searchContainer}>
									<input
										type="text"
										placeholder={`Search ${label}`}
										value={searchTerms[filterName] || ""}
										onChange={(e) =>
											handleSearchChange(filterName, e.target.value)
										}
										style={styles.searchInput}
									/>
								</div>{" "}
								{filteredOptions.length === 0 ? (
									<div
										style={{
											padding: "8px",
											color: "#999",
											fontStyle: "italic",
										}}
									>
										No results found
									</div>
								) : (
									<>
										<div
											style={{
												padding: "4px 12px 6px",
												borderBottom: "1px solid #eee",
												display: "flex",
												alignItems: "center",
												cursor: "pointer",
												fontSize: "12px",
												fontFamily: "MAN EUROPE_Bold",
												color: "#cc0000",
												gap: "6px",
											}}
											onClick={() =>
												handleSelectAll(filterName, filteredOptions)
											}
										>
											<input
												type="checkbox"
												readOnly
												checked={filteredOptions.every((o) =>
													(selectedFilters[filterName] || []).includes(o),
												)}
												style={{ ...styles.checkbox, accentColor: "#cc0000" }}
											/>
											Select All ({filteredOptions.length})
										</div>
										{filteredOptions.map((option) => (
											<div
												key={option}
												style={styles.checkboxItem}
												onClick={() => handleFilterToggle(filterName, option)}
												role="option"
												aria-selected={(
													selectedFilters[filterName] || []
												).includes(option)}
												tabIndex={0}
												onKeyDown={(e) =>
													e.key === "Enter" &&
													handleFilterToggle(filterName, option)
												}
											>
												<input
													type="checkbox"
													checked={(selectedFilters[filterName] || []).includes(
														option,
													)}
													onChange={() => {}}
													style={styles.checkbox}
												/>
												<span>{option}</span>
											</div>
										))}
										<div
											style={{
												padding: "8px 12px",
												borderTop: "1px solid #eee",
												backgroundColor: "#f9f9f9",
												fontSize: "12px",
												color: "#666",
												textAlign: "center",
											}}
										>
											{filteredOptions.length} option
											{filteredOptions.length !== 1 ? "s" : ""} available
										</div>
									</>
								)}
							</div>
						)}
					</div>
				</div>
			);
		};

		// Special renderer for Status Color dropdown with color indicators
		const renderAmpelDropdown = (
			filterName,
			options,
			label,
			showLoadingDot = false,
		) => {
			const searchValue = debouncedSearchTerms[filterName] || "";
			const safeOptions = Array.isArray(options) ? options : [];
			const filteredOptions = searchValue
				? safeOptions.filter((option) => {
						const optionText = option.toLowerCase();
						const labelText = getStatusColorLabel(option).toLowerCase();
						const searchText = searchValue.toLowerCase();
						return (
							optionText.includes(searchText) || labelText.includes(searchText)
						);
					})
				: safeOptions;
			const _hasProgramm = (selectedFilters.programm || []).length > 0;
			const _hasProjNum =
				filterName !== "projectNumber" &&
				(selectedFilters.projectNumber || []).length > 0;
			const isDynamic =
				DYNAMIC_FILTER_KEYS.includes(filterName) &&
				(_hasProgramm || _hasProjNum);
			const dynamicTitle =
				_hasProgramm && _hasProjNum
					? "Filtered by programm & project no."
					: _hasProjNum
						? "Filtered by project no."
						: "Filtered by programm";

			return (
				<div style={styles.filterGroup}>
					<label
						style={{
							...styles.filterLabel,
							display: "flex",
							alignItems: "center",
							flexWrap: "wrap",
							gap: "4px",
						}}
					>
						{label}
						{isDynamic && (
							<Filter
								style={{
									width: "12px",
									height: "12px",
									color: "#cc0000",
									marginLeft: "4px",
									flexShrink: 0,
								}}
								title={dynamicTitle}
							/>
						)}
						<SecondaryLoadingDot show={showLoadingDot} />
					</label>
					<div style={styles.customDropdown} className="custom-dropdown">
						<div
							style={styles.dropdownButton}
							onClick={() => toggleDropdown(filterName)}
							role="button"
							tabIndex={0}
							onKeyDown={(e) => e.key === "Enter" && toggleDropdown(filterName)}
						>
							<span
								style={{
									color:
										(selectedFilters[filterName] || []).length === 0
											? "#999"
											: "#333",
									display: "inline-block",
									width: "calc(100% - 20px)",
								}}
							>
								{getFilterDropdownText(filterName)}
							</span>
							<span style={{ float: "right", marginTop: "2px" }}>
								<ChevronDown
									style={{ width: "16px", height: "16px", color: "#666" }}
								/>
							</span>
						</div>
						{openDropdowns[filterName] && (
							<div style={styles.dropdownContent}>
								<div style={styles.searchContainer}>
									<input
										type="text"
										placeholder={`Search ${label}`}
										value={searchTerms[filterName] || ""}
										onChange={(e) =>
											handleSearchChange(filterName, e.target.value)
										}
										style={styles.searchInput}
									/>
								</div>{" "}
								{filteredOptions.length === 0 ? (
									<div
										style={{
											padding: "8px",
											color: "#999",
											fontStyle: "italic",
										}}
									>
										No results found
									</div>
								) : (
									<>
										<div
											style={{
												padding: "4px 12px 6px",
												borderBottom: "1px solid #eee",
												display: "flex",
												alignItems: "center",
												cursor: "pointer",
												fontSize: "12px",
												fontFamily: "MAN EUROPE_Bold",
												color: "#cc0000",
												gap: "6px",
											}}
											onClick={() =>
												handleSelectAll(filterName, filteredOptions)
											}
										>
											<input
												type="checkbox"
												readOnly
												checked={filteredOptions.every((o) =>
													(selectedFilters[filterName] || []).includes(o),
												)}
												style={{ ...styles.checkbox, accentColor: "#cc0000" }}
											/>
											Select All ({filteredOptions.length})
										</div>
										{filteredOptions.map((option) => (
											<div
												key={option}
												style={styles.checkboxItem}
												onClick={() => handleFilterToggle(filterName, option)}
												role="option"
												aria-selected={(
													selectedFilters[filterName] || []
												).includes(option)}
												tabIndex={0}
												onKeyDown={(e) =>
													e.key === "Enter" &&
													handleFilterToggle(filterName, option)
												}
											>
												<input
													type="checkbox"
													checked={(selectedFilters[filterName] || []).includes(
														option,
													)}
													onChange={() => {}}
													style={styles.checkbox}
												/>{" "}
												<div
													style={{
														display: "flex",
														alignItems: "center",
														gap: "8px",
													}}
												>
													{/* Color indicator dot */}
													<div
														style={{
															width: "12px",
															height: "12px",
															borderRadius: "50%",
															backgroundColor: getAmpelColor(option),
															border:
																getAmpelColor(option) === "transparent"
																	? "1px solid #ccc"
																	: "none",
															flexShrink: 0,
														}}
													/>{" "}
													<div
														style={{
															display: "flex",
															flexDirection: "column",
															gap: "2px",
														}}
													>
														<span style={{ fontWeight: "500" }}>
															{option.toLowerCase() === "available"
																? "Available"
																: getStatusColorLabel(option) || option}
														</span>
													</div>
												</div>
											</div>
										))}
										{/* Summary footer showing options count */}
										<div
											style={{
												padding: "8px 12px",
												borderTop: "1px solid #eee",
												backgroundColor: "#f9f9f9",
												fontSize: "12px",
												color: "#666",
												textAlign: "center",
											}}
										>
											{filteredOptions.length} option
											{filteredOptions.length !== 1 ? "s" : ""} available
										</div>
									</>
								)}
							</div>
						)}
					</div>
				</div>
			);
		}; // Special render function for ruckmeldeStat with "Blank" option support
		const renderRucmeldeStatDropdown = (
			filterName,
			options,
			label,
			showLoadingDot = false,
		) => {
			const searchValue = debouncedSearchTerms[filterName] || "";
			const safeOptions = Array.isArray(options) ? options : [];
			const filteredOptions = searchValue
				? safeOptions.filter(
						(option) =>
							option &&
							option.toLowerCase().includes(searchValue.toLowerCase()),
					)
				: safeOptions;
			const _hasProgramm = (selectedFilters.programm || []).length > 0;
			const _hasProjNum =
				filterName !== "projectNumber" &&
				(selectedFilters.projectNumber || []).length > 0;
			const isDynamic =
				DYNAMIC_FILTER_KEYS.includes(filterName) &&
				(_hasProgramm || _hasProjNum);
			const dynamicTitle3 =
				_hasProgramm && _hasProjNum
					? "Filtered by programm & project no."
					: _hasProjNum
						? "Filtered by project no."
						: "Filtered by programm";

			return (
				<div style={styles.filterGroup}>
					<label
						style={{
							...styles.filterLabel,
							display: "flex",
							alignItems: "center",
							flexWrap: "wrap",
							gap: "4px",
						}}
					>
						{label}
						{isDynamic && (
							<Filter
								style={{
									width: "12px",
									height: "12px",
									color: "#cc0000",
									marginLeft: "4px",
									flexShrink: 0,
								}}
								title={dynamicTitle3}
							/>
						)}
						<SecondaryLoadingDot show={showLoadingDot} />
					</label>
					<div style={styles.customDropdown} className="custom-dropdown">
						<div
							style={styles.dropdownButton}
							onClick={() => toggleDropdown(filterName)}
							role="button"
							tabIndex={0}
							onKeyDown={(e) => e.key === "Enter" && toggleDropdown(filterName)}
						>
							<span
								style={{
									color:
										(selectedFilters[filterName] || []).length === 0
											? "#999"
											: "#333",
									display: "inline-block",
									width: "calc(100% - 20px)",
								}}
							>
								{getFilterDropdownText(filterName)}
							</span>
							<span style={{ float: "right", marginTop: "2px" }}>
								<ChevronDown
									style={{ width: "16px", height: "16px", color: "#666" }}
								/>
							</span>
						</div>
						{openDropdowns[filterName] && (
							<div style={styles.dropdownContent}>
								<div style={styles.searchContainer}>
									<input
										type="text"
										placeholder={`Search ${label}`}
										value={searchTerms[filterName] || ""}
										onChange={(e) =>
											handleSearchChange(filterName, e.target.value)
										}
										style={styles.searchInput}
									/>
								</div>{" "}
								{filteredOptions.length === 0 ? (
									<div
										style={{
											padding: "8px",
											color: "#999",
											fontStyle: "italic",
										}}
									>
										No results found
									</div>
								) : (
									<>
										<div
											style={{
												padding: "4px 12px 6px",
												borderBottom: "1px solid #eee",
												display: "flex",
												alignItems: "center",
												cursor: "pointer",
												fontSize: "12px",
												fontFamily: "MAN EUROPE_Bold",
												color: "#cc0000",
												gap: "6px",
											}}
											onClick={() =>
												handleSelectAll(filterName, filteredOptions)
											}
										>
											<input
												type="checkbox"
												readOnly
												checked={filteredOptions.every((o) =>
													(selectedFilters[filterName] || []).includes(o),
												)}
												style={{ ...styles.checkbox, accentColor: "#cc0000" }}
											/>
											Select All ({filteredOptions.length})
										</div>
										{filteredOptions.map((option) => (
											<div
												key={option}
												style={{
													...styles.checkboxItem,
													// Special styling for "Blank" option
												}}
												onClick={() => handleFilterToggle(filterName, option)}
												role="option"
												aria-selected={(
													selectedFilters[filterName] || []
												).includes(option)}
												tabIndex={0}
												onKeyDown={(e) =>
													e.key === "Enter" &&
													handleFilterToggle(filterName, option)
												}
											>
												<input
													type="checkbox"
													checked={(selectedFilters[filterName] || []).includes(
														option,
													)}
													onChange={() => {}}
													style={styles.checkbox}
												/>
												<div
													style={{
														display: "flex",
														alignItems: "center",
														gap: "8px",
													}}
												>
													{option === "Blank" && (
														<span
															style={{
																fontSize: "12px",
																color: "#6c757d",
															}}
														>
															-
														</span>
													)}
													<span>{option === "Blank" ? "" : option}</span>
												</div>
											</div>
										))}
										<div
											style={{
												padding: "8px 12px",
												borderTop: "1px solid #eee",
												backgroundColor: "#f9f9f9",
												fontSize: "12px",
												color: "#666",
												textAlign: "center",
											}}
										>
											{filteredOptions.length} option
											{filteredOptions.length !== 1 ? "s" : ""} available
										</div>
									</>
								)}
							</div>
						)}
					</div>
				</div>
			);
		};

		// Render the Emcos Milestones section with grouped status filters
		const renderEmcosMilestonesSection = () => {
			const statusFilters = [
				{
					key: "purchasing",
					label: "Purchasing",
					options: filterOptions.purchasingStatuses || [],
				},
				{
					key: "logistics2",
					label: "Logistics",
					options: filterOptions.logistics2Statuses || [],
				},
			];

			return (
				<div style={{ ...styles.filterGroup, marginBottom: "25px" }}>
					<label style={styles.filterLabel}>Emcos Milestones</label>{" "}
					<div
						style={{
							marginLeft: "10px",
							borderLeft: "2px solid #ccc",
							paddingLeft: "10px",
						}}
					>
						{statusFilters.map(({ key, label, options }) => (
							<div key={key} style={{ marginBottom: "12px" }}>
								{renderStatusDropdown(key, options, label, isSecondaryLoading)}
							</div>
						))}
					</div>
				</div>
			);
		}; // Render the Responsible section with Parts filter only
		const renderResponsibleSection = () => {
			return (
				<div style={{ ...styles.filterGroup, marginBottom: "25px" }}>
					<label style={styles.filterLabel}>Responsible</label>
					<div
						style={{
							marginLeft: "10px",
							borderLeft: "2px solid #ccc",
							paddingLeft: "10px",
						}}
					>
						{renderStatusDropdown(
							"responsibleParts",
							getFilteredOptions("responsibleParts"),
							"Person",
							isSecondaryLoading,
						)}
					</div>
				</div>
			);
		}; // Render simple dropdown for static options (flagging, archive)
		const renderSimpleDropdown = (
			filterName,
			options,
			label,
			showLoadingDot = false,
		) => {
			return (
				<div style={styles.filterGroup}>
					<label
						style={{
							...styles.filterLabel,
							display: "flex",
							alignItems: "center",
						}}
					>
						{label}
						<SecondaryLoadingDot show={showLoadingDot} />
					</label>
					<div style={styles.customDropdown} className="custom-dropdown">
						<div
							style={styles.dropdownButton}
							onClick={() => toggleDropdown(filterName)}
							role="button"
							tabIndex={0}
							onKeyDown={(e) => e.key === "Enter" && toggleDropdown(filterName)}
						>
							<span
								style={{
									color:
										(selectedFilters[filterName] || []).length === 0
											? "#999"
											: "#333",
									display: "inline-block",
									width: "calc(100% - 20px)",
								}}
							>
								{getFilterDropdownText(filterName)}
							</span>
							<span style={{ float: "right", marginTop: "2px" }}>
								<ChevronDown
									style={{ width: "16px", height: "16px", color: "#666" }}
								/>
							</span>
						</div>{" "}
						{openDropdowns[filterName] && (
							<div style={styles.dropdownContent}>
								{options.length > 0 && (
									<div
										style={{
											padding: "4px 12px 6px",
											borderBottom: "1px solid #eee",
											display: "flex",
											alignItems: "center",
											cursor: "pointer",
											fontSize: "12px",
											fontFamily: "MAN EUROPE_Bold",
											color: "#cc0000",
											gap: "6px",
										}}
										onClick={() => handleSelectAll(filterName, options)}
									>
										<input
											type="checkbox"
											readOnly
											checked={options.every((o) =>
												(selectedFilters[filterName] || []).includes(o),
											)}
											style={{ ...styles.checkbox, accentColor: "#cc0000" }}
										/>
										Select All ({options.length})
									</div>
								)}
								{options.map((option) => (
									<div
										key={option}
										style={styles.checkboxItem}
										onClick={() => handleFilterToggle(filterName, option)}
										role="option"
										aria-selected={(selectedFilters[filterName] || []).includes(
											option,
										)}
										tabIndex={0}
										onKeyDown={(e) =>
											e.key === "Enter" &&
											handleFilterToggle(filterName, option)
										}
									>
										<input
											type="checkbox"
											checked={(selectedFilters[filterName] || []).includes(
												option,
											)}
											onChange={() => {}}
											style={styles.checkbox}
										/>
										<span>{option}</span>
									</div>
								))}
							</div>
						)}
					</div>
				</div>
			);
		}; // Render the Status section with Parts status sub-filter only
		const renderStatusSection = () => {
			return (
				<div style={{ ...styles.filterGroup, marginBottom: "25px" }}>
					<div style={{ marginLeft: "10px" }}>
						<div style={{ marginBottom: "12px" }}>
							{renderRucmeldeStatDropdown(
								"partsStatus",
								filterOptions.partsStatus || [],
								"Parts Status",
								isSecondaryLoading,
							)}
						</div>
						<div style={{ marginBottom: "12px" }}>
							{renderRucmeldeStatDropdown(
								"seTeam",
								filterOptions.seTeam || [],
								"SET",
								isSecondaryLoading,
							)}
						</div>
					</div>
				</div>
			);
		}; // Render the Flagging and Archive section
		const renderFlaggingArchiveSection = () => {
			return (
				<div style={{ ...styles.filterGroup, marginBottom: "25px" }}>
					<div style={{ marginBottom: "12px" }}>
						{renderSimpleDropdown(
							"flagging",
							filterOptions.flagging || [],
							"Flagging",
							isSecondaryLoading,
						)}
					</div>
					<div style={{ marginBottom: "12px" }}>
						{/* Archive options are always static — no spinner needed */}
						{renderSimpleDropdown("archive", ARCHIVE_OPTIONS, "Archive")}
					</div>
				</div>
			);
		};

		// Render the Requirement Date section with date range picker
		const renderRequirementDateSection = () => {
			return (
				<div style={{ ...styles.filterGroup, marginBottom: "25px" }}>
					{" "}
					<label style={styles.filterLabel}>
						<Calendar
							size={16}
							style={{ marginRight: "8px", verticalAlign: "middle" }}
						/>
						Requirement Date
					</label>
					<LocalizationProvider
						dateAdapter={AdapterDateFns}
						adapterLocale={enGB}
					>
						<div
							style={{
								marginLeft: "10px",
								borderLeft: "2px solid #ccc",
								paddingLeft: "10px",
							}}
						>
							{" "}
							{/* From Date */}
							<div style={{ marginBottom: "12px" }}>
								<label
									style={{
										...styles.filterLabel,
										fontSize: "12px",
										marginBottom: "4px",
									}}
								>
									From Date
								</label>
								<DatePicker
									value={requirementDateFrom}
									onChange={(newDate) => {
										setRequirementDateFrom(newDate);
										setSelectedFilters((prev) => ({
											...prev,
											requirementDateFrom: newDate,
										}));
									}}
									slotProps={{
										textField: {
											size: "small",
											placeholder: "Select from date",
											sx: {
												width: "90%",
												"& .MuiInputBase-root": {
													fontSize: "12px",
													height: "32px",
													fontFamily: "MAN EUROPE_Regular",
												},
												"& .MuiInputBase-input": {
													fontSize: "12px",
													padding: "6px 8px",
												},
												"& .MuiIconButton-root": {
													opacity: 1,
													visibility: "visible",
												},
											},
										},
										field: {
											clearable: true,
											onClear: () => {
												setRequirementDateFrom(null);
												setSelectedFilters((prev) => ({
													...prev,
													requirementDateFrom: null,
												}));
											},
										},
										popper: {
											sx: {
												"& .MuiPaper-root": {
													fontSize: "12px",
													"& .MuiPickersCalendarHeader-root": {
														fontSize: "12px",
														minHeight: "32px",
													},
													"& .MuiPickersArrowSwitcher-root": {
														"& .MuiIconButton-root": {
															fontSize: "14px",
															padding: "4px",
															color: "#cc0000",
														},
													},
													"& .MuiPickersDay-root": {
														fontSize: "12px",
														width: "28px",
														height: "28px",
														"&:hover": {
															backgroundColor: "#cc000020",
														},
														"&.Mui-selected": {
															backgroundColor: "#cc0000",
															color: "white",
														},
													},
													"& .MuiPickersDay-today": {
														border: "1px solid #cc0000",
													},
												},
											},
										},
									}}
								/>
							</div>{" "}
							{/* To Date */}
							<div style={{ marginBottom: "12px" }}>
								<label
									style={{
										...styles.filterLabel,
										fontSize: "12px",
										marginBottom: "4px",
									}}
								>
									To Date
								</label>
								<DatePicker
									value={requirementDateTo}
									onChange={(newDate) => {
										setRequirementDateTo(newDate);
										setSelectedFilters((prev) => ({
											...prev,
											requirementDateTo: newDate,
										}));
									}}
									slotProps={{
										textField: {
											size: "small",
											placeholder: "Select to date",
											sx: {
												width: "90%",
												"& .MuiInputBase-root": {
													fontSize: "12px",
													height: "32px",
													fontFamily: "MAN EUROPE_Regular",
												},
												"& .MuiInputBase-input": {
													fontSize: "12px",
													padding: "6px 8px",
												},
												"& .MuiIconButton-root": {
													opacity: 1,
													visibility: "visible",
												},
											},
										},
										field: {
											clearable: true,
											onClear: () => {
												setRequirementDateTo(null);
												setSelectedFilters((prev) => ({
													...prev,
													requirementDateTo: null,
												}));
											},
										},
										popper: {
											sx: {
												"& .MuiPaper-root": {
													fontSize: "12px",
													"& .MuiPickersCalendarHeader-root": {
														fontSize: "12px",
														minHeight: "32px",
													},
													"& .MuiPickersArrowSwitcher-root": {
														"& .MuiIconButton-root": {
															fontSize: "14px",
															padding: "4px",
															color: "#cc0000",
														},
													},
													"& .MuiPickersDay-root": {
														fontSize: "12px",
														width: "28px",
														height: "28px",
														"&:hover": {
															backgroundColor: "#cc000020",
														},
														"&.Mui-selected": {
															backgroundColor: "#cc0000",
															color: "white",
														},
													},
													"& .MuiPickersDay-today": {
														border: "1px solid #cc0000",
													},
												},
											},
										},
									}}
								/>
							</div>
							{/* Show selected date range */}
							{(requirementDateFrom || requirementDateTo) && (
								<div
									style={{
										fontSize: "11px",
										color: "#666",
										fontFamily: "MAN EUROPE_Regular",
										marginTop: "8px",
										padding: "4px 8px",
										backgroundColor: "#f8f8f8",
										borderRadius: "4px",
									}}
								>
									<div>
										From:{" "}
										{requirementDateFrom
											? format(requirementDateFrom, "dd/MM/yyyy")
											: "Not set"}
									</div>
									<div>
										To:{" "}
										{requirementDateTo
											? format(requirementDateTo, "dd/MM/yyyy")
											: "Not set"}
									</div>
								</div>
							)}
						</div>
					</LocalizationProvider>
				</div>
			);
		};

		// Render the Parts Followup Date section with date range picker
		const renderPartsFollowupDateSection = () => {
			return (
				<div style={{ ...styles.filterGroup, marginBottom: "25px" }}>
					{" "}
					<label style={styles.filterLabel}>
						<Calendar
							size={16}
							style={{ marginRight: "8px", verticalAlign: "middle" }}
						/>
						Parts Followup Date
					</label>
					<LocalizationProvider
						dateAdapter={AdapterDateFns}
						adapterLocale={enGB}
					>
						<div
							style={{
								marginLeft: "10px",
								borderLeft: "2px solid #ccc",
								paddingLeft: "10px",
							}}
						>
							{" "}
							{/* From Date */}
							<div style={{ marginBottom: "12px" }}>
								<label
									style={{
										...styles.filterLabel,
										fontSize: "12px",
										marginBottom: "4px",
									}}
								>
									From Date
								</label>
								<DatePicker
									value={partsFollowupDateFrom}
									onChange={(newDate) => {
										setPartsFollowupDateFrom(newDate);
										setSelectedFilters((prev) => ({
											...prev,
											partsFollowupDateFrom: newDate,
										}));
									}}
									slotProps={{
										textField: {
											size: "small",
											placeholder: "Select from date",
											sx: {
												width: "90%",
												"& .MuiInputBase-root": {
													fontSize: "12px",
													height: "32px",
													fontFamily: "MAN EUROPE_Regular",
												},
												"& .MuiInputBase-input": {
													fontSize: "12px",
													padding: "6px 8px",
												},
												"& .MuiIconButton-root": {
													opacity: 1,
													visibility: "visible",
												},
											},
										},
										field: {
											clearable: true,
											onClear: () => {
												setPartsFollowupDateFrom(null);
												setSelectedFilters((prev) => ({
													...prev,
													partsFollowupDateFrom: null,
												}));
											},
										},
										popper: {
											sx: {
												"& .MuiPaper-root": {
													fontSize: "12px",
													"& .MuiPickersCalendarHeader-root": {
														fontSize: "12px",
														minHeight: "32px",
													},
													"& .MuiPickersArrowSwitcher-root": {
														"& .MuiIconButton-root": {
															fontSize: "14px",
															padding: "4px",
															color: "#cc0000",
														},
													},
													"& .MuiPickersDay-root": {
														fontSize: "12px",
														width: "28px",
														height: "28px",
														"&:hover": {
															backgroundColor: "#cc000020",
														},
														"&.Mui-selected": {
															backgroundColor: "#cc0000",
															color: "white",
														},
													},
													"& .MuiPickersDay-today": {
														border: "1px solid #cc0000",
													},
												},
											},
										},
									}}
								/>
							</div>{" "}
							{/* To Date */}
							<div style={{ marginBottom: "12px" }}>
								<label
									style={{
										...styles.filterLabel,
										fontSize: "12px",
										marginBottom: "4px",
									}}
								>
									To Date
								</label>
								<DatePicker
									value={partsFollowupDateTo}
									onChange={(newDate) => {
										setPartsFollowupDateTo(newDate);
										setSelectedFilters((prev) => ({
											...prev,
											partsFollowupDateTo: newDate,
										}));
									}}
									slotProps={{
										textField: {
											size: "small",
											placeholder: "Select to date",
											sx: {
												width: "90%",
												"& .MuiInputBase-root": {
													fontSize: "12px",
													height: "32px",
													fontFamily: "MAN EUROPE_Regular",
												},
												"& .MuiInputBase-input": {
													fontSize: "12px",
													padding: "6px 8px",
												},
												"& .MuiIconButton-root": {
													opacity: 1,
													visibility: "visible",
												},
											},
										},
										field: {
											clearable: true,
											onClear: () => {
												setPartsFollowupDateTo(null);
												setSelectedFilters((prev) => ({
													...prev,
													partsFollowupDateTo: null,
												}));
											},
										},
										popper: {
											sx: {
												"& .MuiPaper-root": {
													fontSize: "12px",
													"& .MuiPickersCalendarHeader-root": {
														fontSize: "12px",
														minHeight: "32px",
													},
													"& .MuiPickersArrowSwitcher-root": {
														"& .MuiIconButton-root": {
															fontSize: "14px",
															padding: "4px",
															color: "#cc0000",
														},
													},
													"& .MuiPickersDay-root": {
														fontSize: "12px",
														width: "28px",
														height: "28px",
														"&:hover": {
															backgroundColor: "#cc000020",
														},
														"&.Mui-selected": {
															backgroundColor: "#cc0000",
															color: "white",
														},
													},
													"& .MuiPickersDay-today": {
														border: "1px solid #cc0000",
													},
												},
											},
										},
									}}
								/>
							</div>
							{/* Show selected date range */}
							{(partsFollowupDateFrom || partsFollowupDateTo) && (
								<div
									style={{
										fontSize: "11px",
										color: "#666",
										fontFamily: "MAN EUROPE_Regular",
										marginTop: "8px",
										padding: "4px 8px",
										backgroundColor: "#f8f8f8",
										borderRadius: "4px",
									}}
								>
									<div>
										From:{" "}
										{partsFollowupDateFrom
											? format(partsFollowupDateFrom, "dd/MM/yyyy")
											: "Not set"}
									</div>
									<div>
										To:{" "}
										{partsFollowupDateTo
											? format(partsFollowupDateTo, "dd/MM/yyyy")
											: "Not set"}
									</div>
								</div>
							)}
						</div>
					</LocalizationProvider>
				</div>
			);
		};

		// Render the LTB Date section with date range picker
		const renderLtbDateSection = () => {
			return (
				<div style={{ ...styles.filterGroup, marginBottom: "25px" }}>
					{" "}
					<label style={styles.filterLabel}>
						<Calendar
							size={16}
							style={{ marginRight: "8px", verticalAlign: "middle" }}
						/>
						LTB Date
					</label>
					<LocalizationProvider
						dateAdapter={AdapterDateFns}
						adapterLocale={enGB}
					>
						<div
							style={{
								marginLeft: "10px",
								borderLeft: "2px solid #ccc",
								paddingLeft: "10px",
							}}
						>
							{" "}
							{/* From Date */}
							<div style={{ marginBottom: "12px" }}>
								<label
									style={{
										...styles.filterLabel,
										fontSize: "12px",
										marginBottom: "4px",
									}}
								>
									From Date
								</label>
								<DatePicker
									value={ltbDateFrom}
									onChange={(newDate) => {
										setLtbDateFrom(newDate);
										setSelectedFilters((prev) => ({
											...prev,
											ltbDateFrom: newDate,
										}));
									}}
									slotProps={{
										textField: {
											size: "small",
											placeholder: "Select from date",
											sx: {
												width: "90%",
												"& .MuiInputBase-root": {
													fontSize: "12px",
													height: "32px",
													fontFamily: "MAN EUROPE_Regular",
												},
												"& .MuiInputBase-input": {
													fontSize: "12px",
													padding: "6px 8px",
												},
												"& .MuiIconButton-root": {
													opacity: 1,
													visibility: "visible",
												},
											},
										},
										field: {
											clearable: true,
											onClear: () => {
												setLtbDateFrom(null);
												setSelectedFilters((prev) => ({
													...prev,
													ltbDateFrom: null,
												}));
											},
										},
										popper: {
											sx: {
												"& .MuiPaper-root": {
													fontSize: "12px",
													"& .MuiPickersCalendarHeader-root": {
														fontSize: "12px",
														minHeight: "32px",
													},
													"& .MuiPickersArrowSwitcher-root": {
														"& .MuiIconButton-root": {
															fontSize: "14px",
															padding: "4px",
															color: "#cc0000",
														},
													},
													"& .MuiPickersDay-root": {
														fontSize: "12px",
														width: "28px",
														height: "28px",
														"&:hover": {
															backgroundColor: "#cc000020",
														},
														"&.Mui-selected": {
															backgroundColor: "#cc0000",
															color: "white",
														},
													},
													"& .MuiPickersDay-today": {
														border: "1px solid #cc0000",
													},
												},
											},
										},
									}}
								/>
							</div>{" "}
							{/* To Date */}
							<div style={{ marginBottom: "12px" }}>
								<label
									style={{
										...styles.filterLabel,
										fontSize: "12px",
										marginBottom: "4px",
									}}
								>
									To Date
								</label>
								<DatePicker
									value={ltbDateTo}
									onChange={(newDate) => {
										setLtbDateTo(newDate);
										setSelectedFilters((prev) => ({
											...prev,
											ltbDateTo: newDate,
										}));
									}}
									slotProps={{
										textField: {
											size: "small",
											placeholder: "Select to date",
											sx: {
												width: "90%",
												"& .MuiInputBase-root": {
													fontSize: "12px",
													height: "32px",
													fontFamily: "MAN EUROPE_Regular",
												},
												"& .MuiInputBase-input": {
													fontSize: "12px",
													padding: "6px 8px",
												},
												"& .MuiIconButton-root": {
													opacity: 1,
													visibility: "visible",
												},
											},
										},
										field: {
											clearable: true,
											onClear: () => {
												setLtbDateTo(null);
												setSelectedFilters((prev) => ({
													...prev,
													ltbDateTo: null,
												}));
											},
										},
										popper: {
											sx: {
												"& .MuiPaper-root": {
													fontSize: "12px",
													"& .MuiPickersCalendarHeader-root": {
														fontSize: "12px",
														minHeight: "32px",
													},
													"& .MuiPickersArrowSwitcher-root": {
														"& .MuiIconButton-root": {
															fontSize: "14px",
															padding: "4px",
															color: "#cc0000",
														},
													},
													"& .MuiPickersDay-root": {
														fontSize: "12px",
														width: "28px",
														height: "28px",
														"&:hover": {
															backgroundColor: "#cc000020",
														},
														"&.Mui-selected": {
															backgroundColor: "#cc0000",
															color: "white",
														},
													},
													"& .MuiPickersDay-today": {
														border: "1px solid #cc0000",
													},
												},
											},
										},
									}}
								/>
							</div>
							{/* Show selected date range */}
							{(ltbDateFrom || ltbDateTo) && (
								<div
									style={{
										fontSize: "11px",
										color: "#666",
										fontFamily: "MAN EUROPE_Regular",
										marginTop: "8px",
										padding: "4px 8px",
										backgroundColor: "#f8f8f8",
										borderRadius: "4px",
									}}
								>
									<div>
										From:{" "}
										{ltbDateFrom
											? format(ltbDateFrom, "dd/MM/yyyy")
											: "Not set"}
									</div>
									<div>
										To:{" "}
										{ltbDateTo ? format(ltbDateTo, "dd/MM/yyyy") : "Not set"}
									</div>
								</div>
							)}
						</div>
					</LocalizationProvider>
				</div>
			);
		};
		const renderCustomDropdown = (
			filterName,
			options,
			label,
			showLoadingDot = false,
		) => {
			// Use the options passed in (which are already filtered through getFilteredOptions)
			const finalOptions = options;

			// Filter options based on debounced search term
			const searchValue = debouncedSearchTerms[filterName] || "";
			const filteredOptions = searchValue
				? finalOptions.filter((option) =>
						option.toLowerCase().includes(searchValue.toLowerCase()),
					)
				: finalOptions;
			return (
				<div style={styles.filterGroup}>
					{" "}
					<label
						style={{
							...styles.filterLabel,
							display: "flex",
							alignItems: "center",
							flexWrap: "wrap",
							gap: "4px",
						}}
					>
						{label}
						{(() => {
							const _hasProgramm = (selectedFilters.programm || []).length > 0;
							const _hasProjNum =
								filterName !== "projectNumber" &&
								(selectedFilters.projectNumber || []).length > 0;
							const isDynamic =
								DYNAMIC_FILTER_KEYS.includes(filterName) &&
								(_hasProgramm || _hasProjNum);
							if (!isDynamic) return null;
							const dynTitle =
								_hasProgramm && _hasProjNum
									? "Filtered by programm & project no."
									: _hasProjNum
										? "Filtered by project no."
										: "Filtered by programm";
							return (
								<Filter
									style={{
										width: "12px",
										height: "12px",
										color: "#cc0000",
										marginLeft: "4px",
										flexShrink: 0,
									}}
									title={dynTitle}
								/>
							);
						})()}
						<SecondaryLoadingDot show={showLoadingDot} />
					</label>
					<div style={styles.customDropdown} className="custom-dropdown">
						<div
							style={styles.dropdownButton}
							onClick={() => toggleDropdown(filterName)}
							role="button"
							tabIndex={0}
							onKeyDown={(e) => e.key === "Enter" && toggleDropdown(filterName)}
						>
							<span
								style={{
									color:
										(selectedFilters[filterName] || []).length === 0
											? "#999"
											: "#333",
									display: "inline-block",
									width: "calc(100% - 20px)",
								}}
							>
								{getFilterDropdownText(filterName)}
							</span>
							<span style={{ float: "right", marginTop: "2px" }}>
								<ChevronDown
									style={{ width: "16px", height: "16px", color: "#666" }}
								/>
							</span>
						</div>
						{openDropdowns[filterName] && (
							<div style={styles.dropdownContent}>
								<div style={styles.searchContainer}>
									<input
										type="text"
										placeholder={`Search ${label}`}
										value={searchTerms[filterName] || ""}
										onChange={(e) =>
											handleSearchChange(filterName, e.target.value)
										}
										style={styles.searchInput}
									/>
								</div>{" "}
								{filteredOptions.length === 0 ? (
									<div
										style={{
											padding: "8px",
											color: "#999",
											fontStyle: "italic",
										}}
									>
										No results found
									</div>
								) : (
									<>
										<div
											style={{
												padding: "4px 12px 6px",
												borderBottom: "1px solid #eee",
												display: "flex",
												alignItems: "center",
												cursor: "pointer",
												fontSize: "12px",
												fontFamily: "MAN EUROPE_Bold",
												color: "#cc0000",
												gap: "6px",
											}}
											onClick={() =>
												handleSelectAll(filterName, filteredOptions)
											}
										>
											<input
												type="checkbox"
												readOnly
												checked={filteredOptions.every((o) =>
													(selectedFilters[filterName] || []).includes(o),
												)}
												style={{ ...styles.checkbox, accentColor: "#cc0000" }}
											/>
											Select All ({filteredOptions.length})
										</div>
										{filteredOptions.map((option) => (
											<div
												key={option}
												style={styles.checkboxItem}
												onClick={() => handleFilterToggle(filterName, option)}
												role="option"
												aria-selected={(
													selectedFilters[filterName] || []
												).includes(option)}
												tabIndex={0}
												onKeyDown={(e) =>
													e.key === "Enter" &&
													handleFilterToggle(filterName, option)
												}
											>
												<input
													type="checkbox"
													checked={(selectedFilters[filterName] || []).includes(
														option,
													)}
													onChange={() => {}}
													style={styles.checkbox}
												/>{" "}
												<span>{option}</span>
											</div>
										))}
										{/* Summary footer showing options count */}
										<div
											style={{
												padding: "8px 12px",
												borderTop: "1px solid #eee",
												backgroundColor: "#f9f9f9",
												fontSize: "12px",
												color: "#666",
												textAlign: "center",
											}}
										>
											{(() => {
												const isDynamicallyFiltered =
													(filterName === "fzgNr" || filterName === "bza") &&
													selectedFilters.programm &&
													selectedFilters.programm.length > 0;
												const totalOptions =
													filterOptions[
														filterName === "fzgNr"
															? "fzgNr"
															: filterName === "bza"
																? "bza"
																: filterName
													] || [];

												if (
													isDynamicallyFiltered &&
													finalOptions.length !== totalOptions.length
												) {
													return `${filteredOptions.length} of ${finalOptions.length} filtered options shown (${totalOptions.length} total)`;
												} else {
													return `${filteredOptions.length} option${filteredOptions.length !== 1 ? "s" : ""} available`;
												}
											})()}
										</div>
									</>
								)}
							</div>
						)}
					</div>{" "}
				</div>
			);
		};
		// BTT Status Dropdown - filtered to show only specific options
		const renderBttStatusDropdown = (
			filterName,
			options,
			label,
			showLoadingDot = false,
		) => {
			// BTT status uses a fixed allowed-values list for display
			const allowedBttStatusOptions = [
				"AE",
				"BANF",
				"deleted",
				"NR VSC",
				"Inhouse",
				"Scania",
				"NR EV",
				"Lost/Missing Part",
				"In Stock",
				"E missing",
				"Exceptional reportable",
			];
			const displayOptions = allowedBttStatusOptions;
			const searchValue = debouncedSearchTerms[filterName] || "";
			const filteredOptions = searchValue
				? displayOptions.filter((option) =>
						option.toLowerCase().includes(searchValue.toLowerCase()),
					)
				: displayOptions;
			const _hasProgramm = (selectedFilters.programm || []).length > 0;
			const _hasProjNum =
				filterName !== "projectNumber" &&
				(selectedFilters.projectNumber || []).length > 0;
			const isDynamic =
				DYNAMIC_FILTER_KEYS.includes(filterName) &&
				(_hasProgramm || _hasProjNum);
			const dynamicTitle4 =
				_hasProgramm && _hasProjNum
					? "Filtered by programm & project no."
					: _hasProjNum
						? "Filtered by project no."
						: "Filtered by programm";

			return (
				<div style={styles.filterGroup}>
					<label
						style={{
							...styles.filterLabel,
							display: "flex",
							alignItems: "center",
							flexWrap: "wrap",
							gap: "4px",
						}}
					>
						{label}
						{isDynamic && (
							<Filter
								style={{
									width: "12px",
									height: "12px",
									color: "#cc0000",
									marginLeft: "4px",
									flexShrink: 0,
								}}
								title={dynamicTitle4}
							/>
						)}
						<SecondaryLoadingDot show={showLoadingDot} />
					</label>
					<div style={styles.customDropdown} className="custom-dropdown">
						<div
							style={styles.dropdownButton}
							onClick={() => toggleDropdown(filterName)}
							role="button"
							tabIndex={0}
							onKeyDown={(e) => e.key === "Enter" && toggleDropdown(filterName)}
						>
							<span
								style={{
									color:
										(selectedFilters[filterName] || []).length === 0
											? "#999"
											: "#333",
									display: "inline-block",
									width: "calc(100% - 20px)",
								}}
							>
								{getFilterDropdownText(filterName)}
							</span>
							<span style={{ float: "right", marginTop: "2px" }}>
								<ChevronDown
									style={{ width: "16px", height: "16px", color: "#666" }}
								/>
							</span>
						</div>
						{openDropdowns[filterName] && (
							<div style={styles.dropdownContent}>
								<div style={styles.searchContainer}>
									<input
										type="text"
										placeholder={`Search ${label}`}
										value={searchTerms[filterName] || ""}
										onChange={(e) =>
											handleSearchChange(filterName, e.target.value)
										}
										style={styles.searchInput}
									/>
								</div>{" "}
								{filteredOptions.length === 0 ? (
									<div
										style={{
											padding: "8px",
											color: "#999",
											fontStyle: "italic",
										}}
									>
										No results found
									</div>
								) : (
									<>
										<div
											style={{
												padding: "4px 12px 6px",
												borderBottom: "1px solid #eee",
												display: "flex",
												alignItems: "center",
												cursor: "pointer",
												fontSize: "12px",
												fontFamily: "MAN EUROPE_Bold",
												color: "#cc0000",
												gap: "6px",
											}}
											onClick={() =>
												handleSelectAll(filterName, filteredOptions)
											}
										>
											<input
												type="checkbox"
												readOnly
												checked={filteredOptions.every((o) =>
													(selectedFilters[filterName] || []).includes(o),
												)}
												style={{ marginRight: "8px", accentColor: "#cc0000" }}
											/>
											Select All ({filteredOptions.length})
										</div>
										{filteredOptions.map((option) => (
											<div
												key={option}
												style={styles.checkboxItem}
												onClick={() => handleFilterToggle(filterName, option)}
												role="option"
												aria-selected={(
													selectedFilters[filterName] || []
												).includes(option)}
												tabIndex={0}
												onKeyDown={(e) =>
													e.key === "Enter" &&
													handleFilterToggle(filterName, option)
												}
											>
												{" "}
												<input
													type="checkbox"
													checked={(selectedFilters[filterName] || []).includes(
														option,
													)}
													onChange={() => {}}
													style={{ marginRight: "8px" }}
												/>
												<span>{option}</span>
											</div>
										))}
										<div
											style={{
												padding: "4px 8px",
												borderTop: "1px solid #eee",
												backgroundColor: "#f9f9f9",
												fontSize: "12px",
												color: "#666",
												textAlign: "center",
											}}
										>
											{filteredOptions.length} option
											{filteredOptions.length !== 1 ? "s" : ""} available
										</div>
									</>
								)}
							</div>
						)}
					</div>
				</div>
			);
		};
		// Handle clicking on collapsed sidebar to expand it
		const handleCollapsedSidebarClick = () => {
			if (isSidebarCollapsed) {
				setIsSidebarCollapsed(false);
			}
		};

		const renderPetRangePicker = () => {
			const petFrom = selectedFilters.pet?.from || "";
			const petTo = selectedFilters.pet?.to || "";
			const hasValue = petFrom || petTo;

			return (
				<div style={styles.filterGroup}>
					<label style={styles.filterLabel}>PET Range</label>
					<div
						style={{
							display: "flex",
							gap: "6px",
							alignItems: "center",
							width: "90%",
						}}
					>
						<div style={{ flex: 1 }}>
							<label
								style={{
									fontSize: "11px",
									color: "#666",
									display: "block",
									marginBottom: "2px",
									fontFamily: "MAN EUROPE_Regular",
								}}
							>
								From
							</label>
							<input
								type="text"
								placeholder="e.g. 201204"
								value={petFrom}
								onChange={(e) =>
									setSelectedFilters((prev) => ({
										...prev,
										pet: { ...prev.pet, from: e.target.value },
									}))
								}
								style={{
									width: "100%",
									padding: "7px 6px",
									border: `1px solid ${petFrom ? "#cc0000" : "#ddd"}`,
									borderRadius: "4px",
									fontSize: "13px",
									fontFamily: "MAN EUROPE_Regular",
									boxSizing: "border-box",
									outline: "none",
								}}
							/>
						</div>
						<div style={{ flex: 1 }}>
							<label
								style={{
									fontSize: "11px",
									color: "#666",
									display: "block",
									marginBottom: "2px",
									fontFamily: "MAN EUROPE_Regular",
								}}
							>
								To
							</label>
							<input
								type="text"
								placeholder="e.g. 201210"
								value={petTo}
								onChange={(e) =>
									setSelectedFilters((prev) => ({
										...prev,
										pet: { ...prev.pet, to: e.target.value },
									}))
								}
								style={{
									width: "100%",
									padding: "7px 6px",
									border: `1px solid ${petTo ? "#cc0000" : "#ddd"}`,
									borderRadius: "4px",
									fontSize: "13px",
									fontFamily: "MAN EUROPE_Regular",
									boxSizing: "border-box",
									outline: "none",
								}}
							/>
						</div>
					</div>
					{hasValue && (
						<button
							onClick={() =>
								setSelectedFilters((prev) => ({
									...prev,
									pet: { from: "", to: "" },
								}))
							}
							style={{
								marginTop: "4px",
								background: "none",
								border: "none",
								color: "#cc0000",
								fontSize: "11px",
								cursor: "pointer",
								fontFamily: "MAN EUROPE_Regular",
								padding: "0",
							}}
						>
							✕ Clear PET range
						</button>
					)}
				</div>
			);
		};

		const renderAdditionalFiltersModal = () => {
			return (
				<Dialog
					open={isModalOpen}
					onClose={toggleModal}
					maxWidth="lg"
					fullWidth
					disableEnforceFocus
					PaperProps={{
						"data-tour-target": "additional-filters-modal",
						sx: {
							fontFamily: "MAN EUROPE_Regular",
							maxHeight: "90vh",
							borderRadius: "0px",
						},
					}}
				>
					<DialogTitle
						sx={{
							fontFamily: "MAN EUROPE_Bold",
							fontSize: "20px",
							color: "#1e2832",
							borderBottom: "2px solid #cc0000",
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							pb: 2,
						}}
					>
						Additional Filters
						<Button
							onClick={toggleModal}
							data-tour-target="additional-filters-close"
							sx={{
								minWidth: "auto",
								p: 1,
								color: "#666",
								"&:hover": {
									backgroundColor: "#f5f5f5",
								},
							}}
						>
							<X size={20} />
						</Button>
					</DialogTitle>

					<DialogContent sx={{ p: 0 }}>
						<div style={styles.modalContent}>
							<div style={styles.modalFilterGrid}>
								{" "}
								{/* Left Column */}{" "}
								<div>
									{" "}
									{/* Status Color filter */}
									<div style={styles.modalFilterSection}>
										{renderAmpelDropdown(
											"statusColor",
											getFilteredOptions("statusColor"),
											"Status Color",
											isSecondaryLoading,
										)}
									</div>
									{/* Kategorie Teileanflug filter */}
									<div style={styles.modalFilterSection}>
										{renderStatusDropdown(
											"kategorieTeilenanflug",
											getFilteredOptions("kategorieTeilenanflug"),
											"Kategorie Teileanflug",
											isSecondaryLoading,
										)}
									</div>
									{/* Supplier filter */}
									<div style={styles.modalFilterSection}>
										{renderCustomDropdown(
											"supplier",
											getFilteredOptions("supplier"),
											"Supplier",
											isSecondaryLoading,
										)}
									</div>
									{/* Baukasten No filter */}
									<div style={styles.modalFilterSection}>
										{renderRucmeldeStatDropdown(
											"baukastenNo",
											getFilteredOptions("baukastenNo"),
											"Baukasten No",
											isSecondaryLoading,
										)}
									</div>
									{/* BTT Status filter */}
									<div style={styles.modalFilterSection}>
										{renderBttStatusDropdown(
											"statusBtt",
											getFilteredOptions("statusBtt"),
											"BTT Status",
											isSecondaryLoading,
										)}
									</div>
									{/* Ruckmelde Status filter */}
									<div style={styles.modalFilterSection}>
										{renderRucmeldeStatDropdown(
											"ruckmeldeStat",
											getFilteredOptions("ruckmeldeStat"),
											"Ruckmelde Status",
											isSecondaryLoading,
										)}
									</div>
									{/* ALG filter */}
									<div style={styles.modalFilterSection}>
										{renderRucmeldeStatDropdown(
											"alg",
											getFilteredOptions("alg"),
											"ALG",
											isSecondaryLoading,
										)}
									</div>
									{/* PET filter */}
									<div style={styles.modalFilterSection}>
										{renderPetRangePicker()}
									</div>
									{/* Change No filter */}
									<div style={styles.modalFilterSection}>
										{renderRucmeldeStatDropdown(
											"changeNo",
											filterOptions.changeNo || [],
											"Change No",
											isSecondaryLoading,
										)}
									</div>
									{/* Kast Key Anlage filter */}
									<div style={styles.modalFilterSection}>
										{renderRucmeldeStatDropdown(
											"kastKeyAnlage",
											getFilteredOptions("kastKeyAnlage"),
											"Kast Key Anlage",
											isSecondaryLoading,
										)}
									</div>{" "}
									{/* ANLO filter */}
									<div style={styles.modalFilterSection}>
										{renderRucmeldeStatDropdown(
											"anlo",
											[
												"Blank",
												...getFilteredOptions("anlo").filter(
													(o) => o !== "Blank",
												),
											],
											"Anlo",
											isSecondaryLoading,
										)}
									</div>
									{/* VKGR Art filter */}
									<div style={styles.modalFilterSection}>
										{renderRucmeldeStatDropdown(
											"vkgrArt",
											filterOptions.vkgrArt || [],
											"VKGR Art",
											isSecondaryLoading,
										)}
									</div>
									{/* Benenn filter */}
									<div style={styles.modalFilterSection}>
										{renderRucmeldeStatDropdown(
											"benenn",
											filterOptions.benenn || [],
											"Benenn",
											isSecondaryLoading,
										)}
									</div>
                                                                   {/* VKGR Bend filter */}
                                                                   <div style={styles.modalFilterSection}>
                                                                           {renderRucmeldeStatDropdown(
                                                                                   "vkgrBend",
                                                                                   filterOptions.vkgrBend || [],
                                                                                   "VKGR Bend",
                                                                                   isSecondaryLoading,
                                                                           )}
                                                                   </div>
									{/* Flagging and Archive section */}
									<div style={styles.modalFilterSection}>
										{renderFlaggingArchiveSection()}
									</div>
								</div>
								{/* Right Column */}
								<div>
									{/* Status section */}
									<div style={styles.modalFilterSection}>
										{renderStatusSection()}
									</div>
									{/* MB Key filter */}
									<div style={styles.modalFilterSection}>
										<div style={{ marginLeft: "10px" }}>
											{renderRucmeldeStatDropdown(
												"mbKey",
												getFilteredOptions("mbKey"),
												"MB Key",
												isSecondaryLoading,
											)}
										</div>
									</div>
									{/* Tags filter */}
									<div style={styles.modalFilterSection}>
										<div style={{ marginLeft: "10px" }}>
											{renderRucmeldeStatDropdown(
												"tags",
												getFilteredOptions("tags"),
												"Tags",
												isSecondaryLoading,
											)}
										</div>
									</div>
									{/* Parts Followup Date section */}
									<div style={styles.modalFilterSection}>
										{renderPartsFollowupDateSection()}
									</div>

									{/* LTB Date section */}
									<div style={styles.modalFilterSection}>
										{renderLtbDateSection()}
									</div>

									{/* Responsible section */}
									<div style={styles.modalFilterSection}>
										{renderResponsibleSection()}
									</div>

									{/* Requirement Date section */}
									<div style={styles.modalFilterSection}>
										{renderRequirementDateSection()}
									</div>
								</div>
							</div>
						</div>
					</DialogContent>

					<DialogActions
						sx={{
							p: 3,
							borderTop: "1px solid #eee",
							gap: 2,
							justifyContent: "center",
						}}
					>
						{" "}
						<Button
							onClick={applyFilters}
							variant="contained"
							sx={{
								backgroundColor: "#cc0000",
								fontFamily: "MAN EUROPE_Bold",
								borderRadius: "0px",
								"&:hover": {
									backgroundColor: "#aa0000",
								},
								px: 4,
								textTransform: "none",
							}}
						>
							Apply Filters
						</Button>
						<Button
							onClick={clearFilters}
							variant="outlined"
							sx={{
								borderColor: "#1e2832",
								color: "white",
								borderRadius: "0px",
								fontFamily: "MAN EUROPE_Bold",
								backgroundColor: "#1e2832",
								"&:hover": {
									backgroundColor: "#1e2832",
									borderColor: "#1e2832",
								},
								px: 4,
								textTransform: "none",
							}}
						>
							Clear all Filters
						</Button>
					</DialogActions>
				</Dialog>
			);
		};

		const styles = {
			container: {
				width: isSidebarCollapsed ? "50px" : "250px",
				backgroundColor:
					isSidebarCollapsed && isHovered ? "#e8e8e8" : "#f8f8f8",
				padding: 0,
				transition: "all 0.5s ease",
				position: "relative",
				overflow: "hidden",
				paddingBottom: "10px",
				borderRight: "2px solid black",
				borderTop: "2px solid black",
				height: "100%",
				fontFamily: "MAN EUROPE_Regular",
				cursor: isSidebarCollapsed ? "pointer" : "default",
				display: "flex",
				flexDirection: "column",
			},
			header: {
				display: "flex",
				alignItems: "center",
				padding: "10px",
				backgroundColor:
					isSidebarCollapsed && isHovered ? "#e8e8e8" : "#f8f8f8",
				justifyContent: isSidebarCollapsed ? "center" : "space-between",
				flexDirection: isSidebarCollapsed ? "column" : "row",
				transition: "background-color 0.3s ease",
				flexShrink: 0,
			},
			title: {
				fontSize: "16px",
				margin: 0,
				display: "flex",
				alignItems: "center",
				fontFamily: "MAN EUROPE_Bold",
			},
			toggleButton: {
				background: "none",
				border: "none",
				cursor: "pointer",
				padding: "4px",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				borderRadius: "4px",
				transition: "background-color 0.2s ease",
			},
			content: {
				opacity: isSidebarCollapsed ? 0 : 1,
				visibility: isSidebarCollapsed ? "hidden" : "visible",
				transition: "opacity 0.2s ease",
				width: "100%",
				padding: "10px 4px 10px 10px",
				flex: 1,
				overflowY: hasScrollableContent ? "scroll" : "auto",
				overflowX: "hidden",
				maxHeight: "calc(100vh - 74px)",
				minHeight: "300px",
				scrollBehavior: "smooth",
				position: "relative",
				boxSizing: "border-box",
			},
			filterLabel: {
				display: "block",
				fontSize: "14px",
				marginBottom: "6px",
				color: "#333",
				fontFamily: "MAN EUROPE_Bold",
			},
			filterGroup: {
				marginBottom: "15px",
			},
			customDropdown: {
				position: "relative",
				width: "90%",
				marginBottom: "16px",
			},
			dropdownButton: {
				width: "100%",
				padding: "8px",
				border: "1px solid #ddd",
				backgroundColor: "white",
				cursor: "pointer",
				fontFamily: "MAN EUROPE_Regular",
				fontSize: "14px",
				boxSizing: "border-box",
				textAlign: "left",
			},
			dropdownContent: {
				position: "absolute",
				top: "100%",
				left: 0,
				right: 0,
				backgroundColor: "white",
				border: "1px solid #ddd",
				borderTop: "none",
				fontSize: "12px",
				zIndex: 1000,
				maxHeight: "250px",
				overflowY: "auto",
			},
			searchContainer: {
				display: "flex",
				alignItems: "center",
				padding: "8px",
				borderBottom: "1px solid #eee",
				backgroundColor: "#f9f9f9",
			},
			searchInput: {
				width: "100%",
				padding: "6px",
				border: "1px solid #ddd",
				borderRadius: "4px",
				fontSize: "13px",
				fontFamily: "MAN EUROPE_Regular",
				outline: "none",
			},
			checkboxItem: {
				padding: "8px 12px",
				display: "flex",
				alignItems: "center",
				cursor: "pointer",
				fontFamily: "MAN EUROPE_Regular",
			},
			checkbox: {
				marginRight: "8px",
			},
			errorContainer: {
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				padding: "20px",
			},
			retryButton: {
				padding: "8px",
				backgroundColor: "#cc0000",
				border: "1px solid #cc0000",
				cursor: "pointer",
				color: "white",
				borderRadius: "4px",
				fontFamily: "MAN EUROPE_Bold",
			},
			globalSearchContainer: {
				marginBottom: "20px",
			},
			searchInputWrapper: {
				display: "flex",
				alignItems: "center",
				width: "90%",
			},
			globalSearchInput: {
				width: "100%",
				padding: "8px 12px",
				border: "1px solid #ddd",
				borderRadius: "4px 0 0 4px",
				fontSize: "14px",
				fontFamily: "MAN EUROPE_Regular",
				outline: "none",
			},
			searchButton: {
				padding: "8px 12px",
				backgroundColor: "#cc0000",
				border: "none",
				borderRadius: "0 4px 4px 0",
				cursor: "pointer",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				marginTop: "10px",
			},
			accordionContainer: {
				position: "relative",
				display: "flex",
				alignItems: "stretch",
			},
			modalContent: {
				padding: "20px",
			},
			modalFilterGrid: {
				display: "grid",
				gridTemplateColumns: "1fr 1fr",
				gap: "20px",
				marginBottom: "20px",
			},
			modalFilterSection: {
				marginBottom: "25px",
			},
		};

		// Handle the global search functionality
		const [globalSearch, setGlobalSearch] = useState("");
		const handleGlobalSearch = (e) => {
			e.preventDefault();
			// Pass global search term to parent component
			if (onFiltersChange) {
				onFiltersChange({
					...selectedFilters,
					globalSearch: globalSearch,
				});
			}
		}; // Expose clearSidebarState function to parent component
		useImperativeHandle(
			ref,
			() => ({
				clearSidebarState: () => {
					setSelectedFilters({
						programm: [],
						purchasing: [],
						logistics2: [],
						fzgNr: [],
						bza: [],
						partNo: [],
						projectNumber: [],
						partNoPrefix: [],
						supplier: [],
						statusColor: [],
						kategorieTeilenanflug: [],
						ruckmeldeStat: [],
						alg: [],
						seTeam: [],
						pet: { from: "", to: "" },
						vkgrBend: [],
						mbKey: [],
						tags: [],
						responsibleParts: [],
						partsStatus: [],
						flagging: [],
						archive: [],
						requirementDateFrom: null,
						requirementDateTo: null,
						partsFollowupDateFrom: null,
						partsFollowupDateTo: null,
						ltbDateFrom: null,
						ltbDateTo: null,
					});
					setGlobalSearch("");
					setSearchTerms({});
					setDebouncedSearchTerms({});
					setRequirementDateFrom(null);
					setRequirementDateTo(null);
					setPartsFollowupDateFrom(null);
					setPartsFollowupDateTo(null);
					setLtbDateFrom(null);
					setLtbDateTo(null);
				},
			}),
			[],
		);

		return (
			<div style={styles.accordionContainer} data-tour-target="vff-sidebar">
				<div
					style={styles.container}
					onClick={handleCollapsedSidebarClick}
					onMouseEnter={() => setIsHovered(true)}
					onMouseLeave={() => setIsHovered(false)}
					title={isSidebarCollapsed ? "Click to expand filters" : ""}
				>
					<div style={styles.header}>
						{!isSidebarCollapsed && (
							<h2 style={styles.title}>
								<span
									style={{
										marginLeft: "8px",
										fontSize: "25px",
										fontFamily: "MAN EUROPE_Bold",
										fontWeight: "normal",
									}}
								>
									Filters
								</span>
							</h2>
						)}
						<button
							style={styles.toggleButton}
							onClick={(e) => {
								e.stopPropagation();
								setIsSidebarCollapsed(!isSidebarCollapsed);
							}}
							title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
							aria-label={
								isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
							}
							tabIndex={0}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.stopPropagation();
									setIsSidebarCollapsed(!isSidebarCollapsed);
								}
							}}
						>
							{isSidebarCollapsed ? (
								<ChevronRight style={{ width: "20px", height: "20px" }} />
							) : (
								<ChevronLeft style={{ width: "20px", height: "20px" }} />
							)}
						</button>
					</div>{" "}
					<div
						ref={contentRef}
						className="vff-sidebar-content"
						style={styles.content}
						onClick={(e) => !isSidebarCollapsed && e.stopPropagation()}
					>
						{isLoading ? (
							<div
								style={{
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									padding: "20px",
								}}
							>
								<CircularProgress size={24} sx={{ color: "#cc0000" }} />
								<Typography
									variant="body2"
									sx={{ marginTop: "10px", color: "#666" }}
								>
									Loading filters...
								</Typography>
							</div>
						) : error ? (
							<div style={styles.errorContainer}>
								<Typography
									variant="body2"
									sx={{ color: "red", textAlign: "center" }}
								>
									{error}
								</Typography>
								<button style={styles.retryButton} onClick={fetchFilterOptions}>
									Retry
								</button>
							</div>
						) : (
							<>
								{" "}
								{renderCustomDropdown(
									"programm",
									getFilteredOptions("programm"),
									"Programm",
								)}
								{renderCustomDropdown(
									"projectNumber",
									getFilteredOptions("projectNumber"),
									"Project Number",
									false,
								)}
								{/* Emcos Milestones section */}
								{renderEmcosMilestonesSection()}
								{renderCustomDropdown(
									"fzgNr",
									getFilteredOptions("fzgNr"),
									"Fzg Nr",
								)}
								{renderCustomDropdown("bza", getFilteredOptions("bza"), "BZA")}{" "}
								{renderCustomDropdown(
									"partNo",
									filterOptions.partNo || [],
									"Part No",
									isSecondaryLoading,
								)}
								{/* partNoPrefix is always static — no spinner */}
								{renderCustomDropdown(
									"partNoPrefix",
									PART_NO_PREFIXES,
									"Part No Prefix",
								)}
								<div
									style={{
										display: "flex",
										flexDirection: "column",
										gap: "10px",
										width: "90%",
										marginTop: "20px",
									}}
								>
									<button
										onClick={toggleModal}
										data-tour-target="additional-filters-toggle"
										style={{
											padding: "8px",
											backgroundColor: isModalOpen ? "#cc0000" : "#545454",
											color: "white",
											border: "none",
											cursor: "pointer",
											fontFamily: "MAN EUROPE_Bold",
											fontSize: "14px",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											gap: "8px",
											transition: "background-color 0.3s ease",
										}}
										title={
											isModalOpen
												? "Close additional filters"
												: "Open additional filters"
										}
									>
										{" "}
										Additional Filters
										{isSecondaryLoading ? (
											<CircularProgress
												size={13}
												thickness={5}
												sx={{ color: "white", ml: 1 }}
											/>
										) : (
											<ChevronRight
												style={{
													width: "16px",
													height: "16px",
													transform: isModalOpen
														? "rotate(90deg)"
														: "rotate(0deg)",
													transition: "transform 0.3s ease",
												}}
											/>
										)}
									</button>
									<button
										style={{
											padding: "8px",
											backgroundColor: "#cc0000",
											border: "1px solid #cc0000",
											cursor: "pointer",
											color: "white",
											fontFamily: "MAN EUROPE_Bold",
										}}
										onClick={applyFilters}
									>
										Apply Filters
									</button>
									<button
										style={{
											padding: "8px",
											backgroundColor: "#1e2832",
											border: "1px solid #ddd",
											cursor: "pointer",
											color: "#fff",
											fontFamily: "MAN EUROPE_Bold",
										}}
										onClick={clearFilters}
									>
										Clear all Filters{" "}
									</button>
								</div>{" "}
							</>
						)}

						{/* Scroll indicators */}
						{!isSidebarCollapsed &&
							!isLoading &&
							!error &&
							hasScrollableContent && (
								<>
									{canScrollUp && (
										<div
											className="vff-sidebar-scroll-indicator-top"
											style={{
												position: "sticky",
												top: 0,
												left: 0,
												right: 0,
												height: "8px",
												pointerEvents: "none",
												zIndex: 10,
											}}
										/>
									)}
									{canScrollDown && (
										<div
											className="vff-sidebar-scroll-indicator-bottom"
											style={{
												position: "sticky",
												bottom: 0,
												left: 0,
												right: 0,
												height: "8px",
												pointerEvents: "none",
												zIndex: 10,
											}}
										/>
									)}
								</>
							)}
					</div>
				</div>{" "}
				{/* Additional Filters Modal */}
				{renderAdditionalFiltersModal()}
			</div>
		);
	},
);

export default VFFSidebar;
